import {
  countsText,
  type OtherWorkspaceItem,
  type OtherWorkspaceSummary,
} from '@fos/shared/contracts/workspaces-overview';
import { Clock } from 'lucide-react';
import { useId } from 'react';
import { useOpenInWorkspace, useOtherWorkspaces } from '../../../app/layout/other-workspaces';
import { formatDuration, formatMoney } from '../../../lib/format';
import { ListRow } from '../../../ui/ListRow';
import { Mono } from '../../../ui/Mono';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Dot, Status, type Tone } from '../../../ui/Status';

/**
 * „W innych workspace'ach” (DECISIONS 2026-10-08 [S] „Mam coś w drugim workspace'ie”): co czeka na osobę w jej innych
 * workspace'ach — liczone tam, z jej uprawnieniami tam (`identity.otherWorkspaces`). Klik przełącza workspace
 * (istniejące przełączenie) i otwiera rekord. Moje godziny i nierozliczona kwota partnera — tylko do odczytu
 * (godziny są wpisywane raz, w workspace'ie projektu). Widok bez projektu w Figmie — wzorce Dziś (biała sekcja, lista).
 */

function itemTone(i: OtherWorkspaceItem): Tone {
  if (i.kind === 'incident' || i.kind === 'alarm') return 'danger';
  if (i.kind === 'task') return i.meta === 'po terminie' ? 'danger' : 'neutral';
  return 'accent';
}

function hasHours(w: OtherWorkspaceSummary): boolean {
  return Boolean(
    w.hours && (w.hours.seconds > 0 || (w.hours.partnerUnsettled && Number(w.hours.partnerUnsettled) > 0)),
  );
}

function HoursText({ w }: { w: OtherWorkspaceSummary }) {
  const h = w.hours;
  if (!h) return null;
  const unsettled = h.partnerUnsettled && Number(h.partnerUnsettled) > 0 ? h.partnerUnsettled : null;
  return (
    <>
      <Mono>{formatDuration(h.seconds)}</Mono> w tym miesiącu
      {unsettled && (
        <>
          {' · do rozliczenia '}
          <Mono>{formatMoney(unsettled, h.currency)}</Mono>
        </>
      )}
    </>
  );
}

/** Dziś: sekcja tylko, gdy osoba ma kilka workspace'ów i coś czeka (albo ma tam swoje godziny w tym miesiącu). */
export function OtherWorkspacesSection() {
  const { data, multi } = useOtherWorkspaces();
  const open = useOpenInWorkspace();
  const headingId = useId();
  const rows = data.workspaces.filter((w) => w.waiting > 0 || hasHours(w));
  if (!multi || rows.length === 0) return null;
  return (
    <Section aria-labelledby={headingId} className="mt-4 md:mt-6">
      <SectionHeader title={<span id={headingId}>W innych workspace'ach</span>} />
      {rows.map((w) => (
        <div key={w.workspace.id}>
          <ListRow
            onClick={() => open(w.workspace.id, '/dzis')}
            title={w.workspace.name}
            unread={w.waiting > 0}
            description={countsText(w.counts) || 'Nic nie czeka'}
            trailing={<span className="text-body-s text-accent">Przełącz</span>}
            chevron
          />
          {w.items.map((i) => (
            <ListRow
              key={`${i.kind}:${i.to}`}
              onClick={() => open(w.workspace.id, i.to)}
              leading={<Dot tone={itemTone(i)} />}
              title={<span className="font-normal">{i.title}</span>}
              meta={i.meta}
              height="s"
              className="pl-4 md:pl-6"
            />
          ))}
          {hasHours(w) && w.hours && (
            <ListRow
              onClick={() => open(w.workspace.id, w.hours?.reportPath ?? '/czas')}
              leading={<Clock size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
              title={
                <span className="font-normal">
                  Twój czas: <HoursText w={w} />
                </span>
              }
              height="s"
              className="pl-4 md:pl-6"
            />
          )}
        </div>
      ))}
    </Section>
  );
}

/**
 * Czas: „W innych workspace'ach” — moje godziny w bieżącym miesiącu i nierozliczona kwota partnera w innych
 * workspace'ach (tylko do odczytu; raport otwiera się w tamtym workspace'ie). Bez wpisów — sekcji nie ma.
 */
export function OtherWorkspacesHoursSection() {
  const { data, multi } = useOtherWorkspaces();
  const open = useOpenInWorkspace();
  const rows = data.workspaces.filter(hasHours);
  if (!multi || rows.length === 0) return null;
  return (
    <Section title="W innych workspace'ach">
      {rows.map((w) => (
        <ListRow
          key={w.workspace.id}
          onClick={() => open(w.workspace.id, w.hours?.reportPath ?? '/czas')}
          title={w.workspace.name}
          description={<HoursText w={w} />}
          trailing={
            <>
              {w.hours?.partnerUnsettled === '0.00' && <Status tone="success">Rozliczone</Status>}
              <span className="text-body-s text-accent">Raport</span>
            </>
          }
          chevron
        />
      ))}
    </Section>
  );
}
